import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";

const container = document.getElementById("root");
if (!container) {
  throw new Error("No se encontró el elemento #root en index.html");
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

// Registro del Service Worker para PWA / offline support.
//
// Solo registramos en producción (build estático). En dev mode con HMR el
// SW interfiere con la actualización en vivo y rompe debug.
//
// Detección de modo: el build script de producción reemplaza `__PROD__`
// (asignado a globalThis) por el literal `true`. En dev ese reemplazo no
// ocurre, entonces el acceso a globalThis.__PROD__ devuelve undefined y
// el if no entra. Usamos globalThis para evitar ReferenceError si la
// variable no fue definida (un acceso directo a __PROD__ tira error).
const isProd = (globalThis as unknown as { __PROD__?: boolean }).__PROD__ === true;

if (isProd && "serviceWorker" in navigator) {
  // Auto-reload cuando el SW se actualiza (Fase 26).
  //
  // Cada build genera un CACHE_VERSION distinto (timestamp inyectado por
  // scripts/build.ts), por lo que el archivo del SW cambia en cada deploy.
  // El browser detecta el cambio, instala el SW nuevo y, gracias a
  // skipWaiting + clients.claim() en el SW, ese SW toma control inmediato
  // de las pestañas existentes — lo que dispara `controllerchange` acá.
  //
  // Guard `hadControllerOnLoad`: el evento `controllerchange` también
  // dispara la primera vez que el SW se instala (cuando antes no había
  // ningún SW controlando la página). Si recargáramos en ese caso, el
  // primer load entraría en un loop reload→install→reload. La forma
  // canónica de distinguir "primera instalación" de "update real" es
  // mirar si había controller al cargar la página: null = primera vez,
  // != null = update.
  //
  // Guard `refreshing`: ante un update, controllerchange puede dispararse
  // varias veces en sucesión (sobre todo en Chrome con varias pestañas).
  // El flag asegura un único reload por sesión.
  //
  // El listener se setea ANTES del register() para no perder el evento
  // si la activación ocurre antes de que el `load` event corra. El
  // autoguardado de Fase 14 garantiza que el estado del usuario (perfiles,
  // overrides, threshold) está en el bucket — al recargar, la app
  // rehidrata y el usuario puede continuar donde estaba.
  const hadControllerOnLoad = navigator.serviceWorker.controller != null;
  let refreshing = false;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadControllerOnLoad) return; // primera instalación: no recargar
    if (refreshing) return; // ya disparamos un reload en este ciclo
    refreshing = true;
    window.location.reload();
  });

  // Esperamos a 'load' para no competir con la carga inicial de la app.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        console.log("Service Worker registrado, scope:", reg.scope);
      })
      .catch((err) => {
        // Sin caché offline. La app sigue funcionando.
        console.warn("Service Worker registration failed:", err);
      });
  });
}
